import React, { useEffect, useState } from 'react';
import { CheckCircle2, Send, UserRoundX } from 'lucide-react';
import {
  CreatePartnerReferralInputSchema,
  PROPERTY_TYPES,
  type PropertyType,
} from '@valutten/shared-schemas/dist/partnerNetwork';
import { Spinner } from '../../components/common/Spinner';
import type {
  ReferralPartner,
  ReferringBroker,
  TenantCreatePartnerReferralInput,
} from '../../hooks/usePartnerReferrals';
import { parseBudgetDollarsToCents } from '../../utils/partnerReferrals';

const CARD = 'rounded-xl border border-gray-700 bg-gray-800/40 p-4 sm:p-6 space-y-4';
const INPUT =
  'w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none';
const INPUT_ERROR = 'border-red-500/70';
const LABEL = 'block text-sm font-medium text-gray-300 mb-1';

const PROPERTY_TYPE_LABELS: Record<PropertyType, string> = {
  investment: 'Investment',
  owner_occupied: 'Owner occupied',
  commercial: 'Commercial',
};

const GOAL_SUGGESTIONS = [
  'First investment property',
  'Expand an existing portfolio',
  'First home',
  'Next home or upgrade',
  'Rentvesting',
];

const FINANCE_SUGGESTIONS = ['Pre-approval in place', 'Pre-approval in progress', 'Not started'];

type FieldKey =
  | 'referringBrokerDocId'
  | 'partnerId'
  | 'clientName'
  | 'clientEmail'
  | 'clientMobile'
  | 'budget'
  | 'propertyType'
  | 'goal'
  | 'market'
  | 'financeStatus'
  | 'notes'
  | 'clientConsentConfirmed'
  | 'feeDisclosureConfirmed';

type FieldErrors = Partial<Record<FieldKey, string>>;

/** Friendlier wording than zod's defaults for the fields a broker most often trips on. */
const FIELD_MESSAGES: Partial<Record<FieldKey, string>> = {
  referringBrokerDocId: 'Choose the broker this referral is for',
  partnerId: 'Choose a partner',
  clientName: "Enter the client's name",
  clientEmail: 'Enter a valid email address',
  clientConsentConfirmed: 'Confirm your client consented before sending',
  feeDisclosureConfirmed: 'Confirm you disclosed the referral fee before sending',
};

interface FormState {
  referringBrokerDocId: string;
  partnerId: string;
  clientName: string;
  clientEmail: string;
  clientMobile: string;
  budget: string;
  propertyType: PropertyType;
  goal: string;
  market: string;
  financeStatus: string;
  notes: string;
  clientConsentConfirmed: boolean;
  feeDisclosureConfirmed: boolean;
}

const emptyForm = (partnerId: string, referringBrokerDocId: string): FormState => ({
  referringBrokerDocId,
  partnerId,
  clientName: '',
  clientEmail: '',
  clientMobile: '',
  budget: '',
  propertyType: 'investment',
  goal: '',
  market: '',
  financeStatus: '',
  notes: '',
  clientConsentConfirmed: false,
  feeDisclosureConfirmed: false,
});

const orNull = (s: string): string | null => (s.trim() === '' ? null : s);

/**
 * True once the shared (strict) schema carries `referringBrokerDocId`; then the
 * whole payload, broker included, is validated by it. Against an older schema
 * build the field is appended after validation instead, because strict parsing
 * would refuse it as an unknown key.
 */
const SCHEMA_HAS_REFERRING_BROKER = 'referringBrokerDocId' in CreatePartnerReferralInputSchema.shape;

/**
 * Values another surface (the back book radar) opens the form with. Only the
 * client's name and, when it is in the caller's scope, the referring broker;
 * contact details are always typed by the broker.
 */
export interface ReferPrefill {
  clientName: string;
  referringBrokerDocId?: string | null;
}

interface ReferClientFormProps {
  partners: ReferralPartner[];
  prefill?: ReferPrefill | null;
  /** From tenantListReferringBrokers: who the caller may refer on behalf of (a broker gets only themselves). */
  brokers: ReferringBroker[];
  onSubmit: (input: TenantCreatePartnerReferralInput) => Promise<string>;
}

/** The caller's own broker profile when they have one, else the only broker in scope, else none (must choose). */
export function defaultReferringBrokerId(brokers: ReferringBroker[]): string {
  const self = brokers.find((b) => b.isSelf);
  if (self) return self.brokerDocId;
  return brokers.length === 1 ? brokers[0].brokerDocId : '';
}

export const ReferClientForm: React.FC<ReferClientFormProps> = ({ partners, brokers, prefill, onSubmit }) => {
  const soloPartnerId = partners.length === 1 ? partners[0].partnerId : '';
  const defaultBrokerId = defaultReferringBrokerId(brokers);
  const [form, setForm] = useState<FormState>(() => {
    const base = emptyForm(soloPartnerId, defaultBrokerId);
    if (!prefill) return base;
    // A prefilled broker is used only when it is one the caller may refer for.
    const brokerId =
      prefill.referringBrokerDocId && brokers.some((b) => b.brokerDocId === prefill.referringBrokerDocId)
        ? prefill.referringBrokerDocId
        : base.referringBrokerDocId;
    return { ...base, clientName: prefill.clientName, referringBrokerDocId: brokerId };
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  // Partners and brokers load asynchronously; preselect once they arrive.
  useEffect(() => {
    if (soloPartnerId && !form.partnerId) setForm((f) => ({ ...f, partnerId: soloPartnerId }));
  }, [soloPartnerId, form.partnerId]);
  useEffect(() => {
    if (defaultBrokerId && !form.referringBrokerDocId) {
      setForm((f) => ({ ...f, referringBrokerDocId: defaultBrokerId }));
    }
  }, [defaultBrokerId, form.referringBrokerDocId]);

  if (brokers.length === 0) {
    return (
      <div className={CARD}>
        <div className="flex items-start gap-3">
          <UserRoundX className="h-5 w-5 flex-shrink-0 text-gray-400 mt-0.5" />
          <div>
            <h2 className="text-base font-semibold text-gray-100">No brokers to refer for</h2>
            <p className="mt-1 text-sm text-gray-400">
              A referral is made on behalf of a broker, who is owed the referral fee. There are no brokers in your
              scope, so you cannot send referrals yet. You can still follow referrals under My clients.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (partners.length === 0) {
    return (
      <div className={CARD}>
        <h2 className="text-base font-semibold text-gray-100">Refer a client</h2>
        <p className="text-sm text-gray-400">
          No referral partners are available to your company yet. Once a partner is connected, you can refer
          clients to them here.
        </p>
      </div>
    );
  }

  const selectedPartner = partners.find((p) => p.partnerId === form.partnerId) ?? null;
  const selectedBroker = brokers.find((b) => b.brokerDocId === form.referringBrokerDocId) ?? null;
  const partnerName = selectedPartner?.name ?? 'the partner';

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    const errKey: FieldKey = key;
    if (errors[errKey]) setErrors((e) => ({ ...e, [errKey]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSentTo(null);

    const nextErrors: FieldErrors = {};
    if (!selectedBroker) nextErrors.referringBrokerDocId = FIELD_MESSAGES.referringBrokerDocId;
    const budget = parseBudgetDollarsToCents(form.budget);
    if (!budget.ok) nextErrors.budget = budget.error;

    const candidate = {
      partnerId: form.partnerId,
      clientName: form.clientName,
      clientEmail: form.clientEmail,
      clientMobile: form.clientMobile,
      budgetCents: budget.ok ? budget.cents : null,
      propertyType: form.propertyType,
      goal: orNull(form.goal),
      market: orNull(form.market),
      financeStatus: orNull(form.financeStatus),
      notes: orNull(form.notes),
      clientConsentConfirmed: form.clientConsentConfirmed,
      feeDisclosureConfirmed: form.feeDisclosureConfirmed,
      ...(SCHEMA_HAS_REFERRING_BROKER && selectedBroker ? { referringBrokerDocId: selectedBroker.brokerDocId } : {}),
    };
    const parsed = CreatePartnerReferralInputSchema.safeParse(candidate);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        const raw = String(issue.path[0] ?? '');
        const key = (raw === 'budgetCents' ? 'budget' : raw) as FieldKey;
        if (!nextErrors[key]) nextErrors[key] = FIELD_MESSAGES[key] ?? issue.message;
      }
    }
    if (Object.keys(nextErrors).length > 0 || !parsed.success || !selectedBroker) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      await onSubmit({ ...parsed.data, referringBrokerDocId: selectedBroker.brokerDocId });
      setSentTo(partnerName);
      // Keep the partner and broker: an owner often refers several clients for the same broker.
      setForm(emptyForm(soloPartnerId || form.partnerId, form.referringBrokerDocId));
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (key: FieldKey) => `${INPUT} ${errors[key] ? INPUT_ERROR : ''}`;
  const fieldError = (key: FieldKey) =>
    errors[key] ? (
      <p className="mt-1 text-xs text-red-300" role="alert">
        {errors[key]}
      </p>
    ) : null;

  return (
    <form className={CARD} onSubmit={handleSubmit} noValidate>
      <div>
        <h2 className="text-base font-semibold text-gray-100">Refer a client</h2>
        <p className="text-sm text-gray-400">
          Their details go straight to {partnerName}, who will contact them and keep you updated here.
        </p>
      </div>

      {sentTo && (
        <div className="flex items-start gap-2 rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-300">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>Referral sent to {sentTo}. It now appears in My clients.</span>
        </div>
      )}
      {submitError && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300" role="alert">
          {submitError}
        </div>
      )}

      {partners.length > 1 ? (
        <div>
          <label htmlFor="pr-partner" className={LABEL}>Partner</label>
          <select
            id="pr-partner"
            className={inputClass('partnerId')}
            value={form.partnerId}
            onChange={(e) => set('partnerId', e.target.value)}
          >
            <option value="">Choose a partner</option>
            {partners.map((p) => (
              <option key={p.partnerId} value={p.partnerId}>{p.name}</option>
            ))}
          </select>
          {selectedPartner?.tagline && <p className="mt-1 text-xs text-gray-400">{selectedPartner.tagline}</p>}
          {fieldError('partnerId')}
        </div>
      ) : (
        <div className="rounded-lg bg-gray-900/60 px-4 py-3">
          <p className="text-xs text-gray-400">Partner</p>
          <p className="text-sm font-medium text-gray-100">{partners[0].name}</p>
          {partners[0].tagline && <p className="text-xs text-gray-400">{partners[0].tagline}</p>}
        </div>
      )}

      {brokers.length > 1 ? (
        <div>
          <label htmlFor="pr-broker" className={LABEL}>Referring broker</label>
          <select
            id="pr-broker"
            className={inputClass('referringBrokerDocId')}
            value={form.referringBrokerDocId}
            onChange={(e) => set('referringBrokerDocId', e.target.value)}
          >
            <option value="">Choose a broker</option>
            {brokers.map((b) => (
              <option key={b.brokerDocId} value={b.brokerDocId}>
                {b.isSelf ? `${b.name} (you)` : b.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-400">
            The referral fee on settlement is owed to {selectedBroker ? selectedBroker.name : 'the broker you choose'}.
          </p>
          {fieldError('referringBrokerDocId')}
        </div>
      ) : (
        <div className="rounded-lg bg-gray-900/60 px-4 py-3">
          <p className="text-xs text-gray-400">Referring broker</p>
          <p className="text-sm font-medium text-gray-100">{brokers[0].name}</p>
          <p className="text-xs text-gray-400">The referral fee on settlement is owed to {brokers[0].name}.</p>
          {fieldError('referringBrokerDocId')}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="pr-name" className={LABEL}>Client name</label>
          <input
            id="pr-name"
            className={inputClass('clientName')}
            value={form.clientName}
            onChange={(e) => set('clientName', e.target.value)}
            autoComplete="off"
          />
          {fieldError('clientName')}
        </div>
        <div>
          <label htmlFor="pr-mobile" className={LABEL}>Mobile</label>
          <input
            id="pr-mobile"
            type="tel"
            inputMode="tel"
            className={inputClass('clientMobile')}
            value={form.clientMobile}
            onChange={(e) => set('clientMobile', e.target.value)}
            autoComplete="off"
          />
          {fieldError('clientMobile')}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pr-email" className={LABEL}>Email</label>
          <input
            id="pr-email"
            type="email"
            inputMode="email"
            className={inputClass('clientEmail')}
            value={form.clientEmail}
            onChange={(e) => set('clientEmail', e.target.value)}
            autoComplete="off"
          />
          {fieldError('clientEmail')}
        </div>
        <div>
          <label htmlFor="pr-budget" className={LABEL}>Approximate budget (optional)</label>
          <input
            id="pr-budget"
            inputMode="decimal"
            placeholder="$780,000"
            className={inputClass('budget')}
            value={form.budget}
            onChange={(e) => set('budget', e.target.value)}
          />
          {fieldError('budget')}
        </div>
        <div>
          <label htmlFor="pr-property-type" className={LABEL}>Property type</label>
          <select
            id="pr-property-type"
            className={inputClass('propertyType')}
            value={form.propertyType}
            onChange={(e) => set('propertyType', e.target.value as PropertyType)}
          >
            {PROPERTY_TYPES.map((t) => (
              <option key={t} value={t}>{PROPERTY_TYPE_LABELS[t]}</option>
            ))}
          </select>
          {fieldError('propertyType')}
        </div>
        <div>
          <label htmlFor="pr-goal" className={LABEL}>What they are trying to do (optional)</label>
          <input
            id="pr-goal"
            list="pr-goal-options"
            className={inputClass('goal')}
            value={form.goal}
            onChange={(e) => set('goal', e.target.value)}
          />
          <datalist id="pr-goal-options">
            {GOAL_SUGGESTIONS.map((g) => <option key={g} value={g} />)}
          </datalist>
          {fieldError('goal')}
        </div>
        <div>
          <label htmlFor="pr-market" className={LABEL}>Where they are looking (optional)</label>
          <input
            id="pr-market"
            className={inputClass('market')}
            value={form.market}
            onChange={(e) => set('market', e.target.value)}
          />
          {fieldError('market')}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pr-finance" className={LABEL}>Finance position (optional)</label>
          <input
            id="pr-finance"
            list="pr-finance-options"
            className={inputClass('financeStatus')}
            value={form.financeStatus}
            onChange={(e) => set('financeStatus', e.target.value)}
          />
          <datalist id="pr-finance-options">
            {FINANCE_SUGGESTIONS.map((f) => <option key={f} value={f} />)}
          </datalist>
          {fieldError('financeStatus')}
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="pr-notes" className={LABEL}>Anything {partnerName} should know (optional)</label>
          <textarea
            id="pr-notes"
            rows={3}
            className={inputClass('notes')}
            value={form.notes}
            onChange={(e) => set('notes', e.target.value)}
          />
          {fieldError('notes')}
        </div>
      </div>

      <div className="space-y-3 rounded-lg bg-gray-900/60 px-4 py-3">
        <div>
          <label className="flex items-start gap-3 text-sm text-gray-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-blue-600"
              checked={form.clientConsentConfirmed}
              onChange={(e) => set('clientConsentConfirmed', e.target.checked)}
            />
            <span>My client has consented to their details being sent to {partnerName}.</span>
          </label>
          {fieldError('clientConsentConfirmed')}
        </div>
        <div>
          <label className="flex items-start gap-3 text-sm text-gray-200">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 flex-shrink-0 accent-blue-600"
              checked={form.feeDisclosureConfirmed}
              onChange={(e) => set('feeDisclosureConfirmed', e.target.checked)}
            />
            <span>I have disclosed to my client that I receive a referral fee for this referral.</span>
          </label>
          {fieldError('feeDisclosureConfirmed')}
        </div>
        <p className="text-xs text-gray-400" data-testid="collection-notice">
          {partnerName} receives your name, your business and your client&apos;s details to assess and progress the referral and to pay any
          referral fee owed. {partnerName} holds that information; VALUTTEN processes it on {partnerName}&apos;s behalf and never passes it on.
          Gift card rewards are delivered by LocalCard, which receives the recipient&apos;s name and email only.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? <Spinner size="sm" /> : <Send className="h-4 w-4" />}
          {submitting ? 'Sending' : `Send to ${selectedPartner?.shortName || partnerName}`}
        </button>
      </div>
    </form>
  );
};

export default ReferClientForm;
